/**
 * GroupScrapeResults — Display Telegram group/channel export results in Playground
 * Shows participant count, download options, preview table, and Add to graph
 */
import { Button } from '@/components/ui/button'
import { Download, Users, FileJson, X } from 'lucide-react'

interface GroupScrapeResultsProps {
  result: {
    success?: boolean
    group_identifier?: string
    participant_count?: number
    execution_time?: number
    html_file?: { content?: string; file_name?: string }
    json_file?: { content?: string; file_name?: string; participants?: any[] }
    error?: string
    detail?: string
  }
  caseId: string
  onAddToGraph: () => void
  onClose: () => void
}

function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export function GroupScrapeResults({ result, onAddToGraph, onClose }: GroupScrapeResultsProps) {
  const error = result.error || result.detail
  const participants = result.json_file?.participants ?? []
  const count = result.participant_count ?? participants.length

  if (error) {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
          <p className="text-sm font-medium text-red-600 dark:text-red-400">Export failed</p>
          <p className="text-xs text-muted-foreground mt-1">{error}</p>
        </div>
        <Button variant="outline" size="sm" onClick={onClose} className="w-full">
          Close
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-4">
        <p className="text-sm font-medium text-green-600 dark:text-green-400">Export completed</p>
        <p className="text-xs text-muted-foreground mt-1">
          {result.group_identifier} — {count} participant{count !== 1 ? 's' : ''} found
          {result.execution_time && ` (${(result.execution_time / 1000).toFixed(1)}s)`}
        </p>
      </div>

      <div className="flex gap-2">
        {result.html_file?.content && (
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              downloadFile(
                result.html_file!.content!,
                result.html_file?.file_name || 'participants_report.html',
                'text/html'
              )
            }
          >
            <Download className="w-4 h-4 mr-2" />
            HTML
          </Button>
        )}
        {result.json_file?.content && (
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              downloadFile(
                result.json_file!.content!,
                result.json_file?.file_name || 'group_data.json',
                'application/json'
              )
            }
          >
            <FileJson className="w-4 h-4 mr-2" />
            JSON
          </Button>
        )}
      </div>

      {participants.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Users className="w-3.5 h-3.5" />
              Participants (showing first 20)
            </p>
            <Button size="sm" onClick={onAddToGraph}>
              <Users className="w-4 h-4 mr-2" />
              Add to graph
            </Button>
          </div>
          <div className="max-h-64 overflow-auto rounded border text-xs">
            <table className="w-full">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">ID</th>
                  <th className="px-2 py-1.5 text-left font-medium">Username</th>
                  <th className="px-2 py-1.5 text-left font-medium">Name</th>
                  <th className="px-2 py-1.5 text-left font-medium">Phone</th>
                </tr>
              </thead>
              <tbody>
                {participants.slice(0, 20).map((p: any, i: number) => (
                  <tr key={i} className="border-t border-muted/50">
                    <td className="px-2 py-1">{p.id ?? p.user_id ?? '-'}</td>
                    <td className="px-2 py-1 font-mono">
                      {(p.username || '').trim() ? `@${String(p.username).replace(/^@/, '')}` : '-'}
                    </td>
                    <td className="px-2 py-1">
                      {[p.first_name, p.last_name, p.name].filter(Boolean).join(' ') || '-'}
                    </td>
                    <td className="px-2 py-1">{p.phone || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Button variant="ghost" size="sm" onClick={onClose} className="w-full">
        <X className="w-4 h-4 mr-2" />
        Close
      </Button>
    </div>
  )
}
